import { CurrentProject } from "@/hooks/use-project";
import { AccountMetadata } from "@/reducers/environments";
import { LoadedDestinationInfo } from "@/types/global";
import { audienceConnectionStateFromSettings } from "../AudienceConnectionState";
import { RequestableSaasDestination, SaaSDestination } from "../destinations";
import { checkRequiredPropertiesAreAllowed } from "../hooks/useAllRequiredPropertiesAllowed";
import {
  DestinationValidationResult,
  DestinationValidity,
} from "../validation";

function configuredDestRequiresTermsOfServiceSuccess(
  dest: LoadedDestinationInfo,
): boolean {
  return (
    dest.name === "Meta Audiences" ||
    dest.name === "Microsoft Ads Audiences" ||
    dest.name === "LinkedIn Audiences"
  );
}

export function getAudienceDestinationValidity(
  isHipaaV2: boolean,
  project: CurrentProject,
  accountMetadata: AccountMetadata,
  configuredDestination: LoadedDestinationInfo,
  saasDestination?: SaaSDestination | RequestableSaasDestination,
): DestinationValidationResult {
  const settings = configuredDestination.settings || {};
  const audienceConnectionState = audienceConnectionStateFromSettings(settings);
  const hasAllowedAllRequiredProperties = checkRequiredPropertiesAreAllowed(
    isHipaaV2,
    project,
    accountMetadata,
    configuredDestination,
    saasDestination,
  );

  if (
    configuredDestRequiresTermsOfServiceSuccess(configuredDestination) &&
    "termsOfServiceTestState" in settings &&
    settings.termsOfServiceTestState === "tosTestFailed"
  ) {
    return {
      validity: DestinationValidity.totallyInvalidTermsOfServiceTestFailed,
      warningMessage: "Terms of service test failed",
    };
  }

  if (
    audienceConnectionState !== null &&
    audienceConnectionState.status !== "audienceConnectionTestSucceeded"
  ) {
    return {
      validity: DestinationValidity.totallyInvalidAudienceConnectionTestFailed,
      warningMessage: "Audience connection test failed",
    };
  }

  if (isHipaaV2 && !configuredDestination.event_property_sensitivity_levels) {
    return {
      validity: DestinationValidity.totallyInvalidMissingAllowlistWhenHIPAA,
      warningMessage: "Allowlist is missing",
    };
  }

  if (!hasAllowedAllRequiredProperties) {
    return {
      validity: DestinationValidity.totallyInvalidMissingAllowlistWhenHIPAA,
      warningMessage: "Required allowlist properties have not been allowed",
    };
  }

  const connectionIsValid =
    configuredDestination.connection_mode === "server"
      ? configuredDestination.is_valid_server_side
      : configuredDestination.is_valid_client_side;

  if (!configuredDestination.enabled) {
    return {
      validity: DestinationValidity.totallyInvalidDisabled,
      warningMessage: "Destination is not enabled",
    };
  }

  return connectionIsValid
    ? {
        validity: DestinationValidity.valid,
      }
    : {
        validity: DestinationValidity.totallyInvalid,
        warningMessage: "Destination is not valid",
      };
}
